/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Manage users and groups", description = "Invite users, organize them into groups, and control which workspaces each group can access.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Manage users and groups",
        description: "Invite users, organize them into groups, and control which workspaces each group can access.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Users",
          "depth": 0,
          "id": "users"
        }, {
          "value": "Invite users",
          "depth": 1,
          "id": "invite-users"
        }, {
          "value": "Remove users",
          "depth": 1,
          "id": "remove-users"
        }, {
          "value": "Groups",
          "depth": 0,
          "id": "groups"
        }, {
          "value": "Organization admins and workspace admins",
          "depth": 1,
          "id": "organization-admins-and-workspace-admins"
        }, {
          "value": "Assign users to groups",
          "depth": 1,
          "id": "assign-users-to-groups"
        }, {
          "value": "Create a group",
          "depth": 1,
          "id": "create-a-group"
        }, {
          "value": "Grant a group access to a workspace",
          "depth": 1,
          "id": "grant-a-group-access-to-a-workspace"
        }, {
          "value": "Change a group's role from workspace settings",
          "depth": 1,
          "id": "change-a-groups-role-from-workspace-settings"
        }, {
          "value": "Transfer organization admin access",
          "depth": 0,
          "id": "transfer-organization-admin-access"
        }, {
          "value": "Transfer access when an organization admin is available",
          "depth": 1,
          "id": "transfer-access-when-an-organization-admin-is-available"
        }, {
          "value": "Transfer access with SSO and SCIM",
          "depth": 1,
          "id": "transfer-access-with-sso-and-scim"
        }, {
          "value": "Recover access when no organization admins are available",
          "depth": 1,
          "id": "recover-access-when-no-organization-admins-are-available"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      a: "a",
      h2: "h2",
      img: "img",
      h3: "h3",
      ol: "ol",
      li: "li",
      ul: "ul",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["Hightouch manages workspace access through ", _jsx(_components.strong, {
          children: "groups"
        }), ". Add ", _jsx(_components.strong, {
          children: "users"
        }), " to ", _jsx(_components.strong, {
          children: "groups"
        }), ", then assign each group a ", _jsx(_components.strong, {
          children: "role"
        }), " in the workspaces it needs to access. Because access is never granted to individual users, set up users and groups first when you onboard your organization."]
      }), "\n", _jsxs(_components.p, {
        children: ["This guide covers how to ", _jsx(_components.a, {
          href: "#invite-users",
          children: "invite users"
        }), ", ", _jsx(_components.a, {
          href: "#assign-users-to-groups",
          children: "assign users to groups"
        }), ", and ", _jsx(_components.a, {
          href: "#grant-a-group-access-to-a-workspace",
          children: "give groups access to workspaces"
        }), ". To control what a group can do within a workspace, see ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "Roles"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you plan to connect Hightouch to an identity provider such as Okta, review\nthe ", _jsx(_components.a, {
            href: "/workspace-management/sso",
            children: "single sign-on (SSO) documentation"
          }), " before\ninviting users. SAML SSO and SCIM can automate user provisioning and group\nmembership."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "users",
        children: "Users"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Organization settings > Users"
        }), " to view everyone in your organization. The ", _jsx(_components.strong, {
          children: "Members"
        }), " tab shows each user's login method and group memberships. Select a user to view more details."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/all-users-in-org.png",
          alt: "The Users page in Organization settings, showing the Members and Pending invites tabs with each member's login method and groups"
        })
      }), "\n", _jsx(_components.h3, {
        id: "invite-users",
        children: "Invite users"
      }), "\n", _jsxs(_components.p, {
        children: ["If your organization uses SSO, you don't need to invite users manually. Go to ", _jsx(_components.strong, {
          children: "Organization settings > Single sign-on"
        }), ", copy the ", _jsx(_components.strong, {
          children: "Hightouch login URL"
        }), ", and share it with your users. They can use this URL to sign in through your identity provider."]
      }), "\n", _jsxs(_components.p, {
        children: ["If your organization requires SSO or has manual invites turned off, the ", _jsx(_components.strong, {
          children: "Invite user"
        }), " button is disabled and new users must authenticate through SSO."]
      }), "\n", _jsxs(_components.p, {
        children: ["If your organization doesn't use SSO, members of the ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " group can invite users:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings > Users"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Invite user"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter the user's ", _jsx(_components.strong, {
            children: "Email address"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Assign to groups"
          }), ", select the user's groups. Without a group, the user has no workspace access when they first sign in."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Send invite"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/invite-user.png",
          alt: "Inviting a new user to the org"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Invitations that haven't been accepted appear on the ", _jsx(_components.strong, {
          children: "Pending invites"
        }), " tab of ", _jsx(_components.strong, {
          children: "Organization settings > Users"
        }), ", along with how long each has until it expires. Invitations expire after 30 days. To withdraw an invitation, delete it from this tab. An organization can have up to 50 active invitations at a time."]
      }), "\n", _jsx(_components.h3, {
        id: "remove-users",
        children: "Remove users"
      }), "\n", _jsxs(_components.p, {
        children: ["Only members of the ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " group can remove users. Go to ", _jsx(_components.strong, {
          children: "Organization settings > Users"
        }), ", select the user, and click ", _jsx(_components.strong, {
          children: "Remove user"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Removing a user doesn't prevent them from rejoining the organization later.\nThey can rejoin if they receive another invitation or authenticate through\nyour identity provider."
        })
      }), "\n", _jsx(_components.h2, {
        id: "groups",
        children: "Groups"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "group"
        }), " is a collection of users that share workspace access and permissions. Instead of assigning access to individual users, you add users to groups and configure access for each group. This makes it easy to update permissions for many users at once."]
      }), "\n", _jsx(_components.p, {
        children: "Every Hightouch organization includes two built-in groups. You can't delete or rename them, or change their workspace access:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization admins"
          }), " have full access to all workspaces and can manage organization-wide settings, including users, groups, billing, and SSO."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization viewers"
          }), " have read-only access to all workspaces. They can't make changes or manage organization settings."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " group must always have at least one member. Using the ", _jsx(_components.strong, {
          children: "Organization viewers"
        }), " group is optional."]
      }), "\n", _jsx(_components.h3, {
        id: "organization-admins-and-workspace-admins",
        children: "Organization admins and workspace admins"
      }), "\n", _jsx(_components.p, {
        children: "Workspace admin and organization admin are different levels of access, even though both include \"admin\":"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Organization admin"
          }), " means belonging to the ", _jsx(_components.strong, {
            children: "Organization admins"
          }), " group. It controls users, groups, billing, and SSO across the whole organization."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace admin"
          }), " is a role a group holds in a single workspace. It gives full control over that workspace's resources and settings, but not over users or groups."]
        }), "\n"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Action"
            }), _jsx(_components.th, {
              children: "Organization admin"
            }), _jsx(_components.th, {
              children: "Workspace admin"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Invite or remove users"
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create a group"
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Add users to a group or remove them"
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Give a group access to a workspace"
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "No"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Change the role of a group that already has access to a workspace"
            }), _jsx(_components.td, {
              children: "Yes"
            }), _jsx(_components.td, {
              children: "Only in workspaces they administer"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "assign-users-to-groups",
        children: "Assign users to groups"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Only organization admins can add users to groups or remove them. Workspace\nadmins can't, even in workspaces they administer."
        })
      }), "\n", _jsx(_components.p, {
        children: "If you use SSO group mappings, you can manage group membership through your identity provider. Otherwise, or if you need groups that your identity provider doesn't manage, you can assign users to groups directly in Hightouch."
      }), "\n", _jsx(_components.p, {
        children: "To update groups for one user:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings > Users"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the user."
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Group assignments"
          }), ", use the ", _jsx(_components.strong, {
            children: "Assigned"
          }), " toggle to add or remove the user from each group."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/add-user-to-groups.png",
          alt: "A user's Group assignments, with a toggle to assign each group"
        })
      }), "\n", _jsx(_components.p, {
        children: "To manage multiple users in a group:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings > Groups"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the group."
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Members"
          }), " tab."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/group-members.png",
          alt: "List of all users in a group"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["To add members, click ", _jsx(_components.strong, {
            children: "Add users"
          }), ", select the users, and click the ", _jsx(_components.strong, {
            children: "Assign"
          }), " button. To remove members, select them and click the ", _jsx(_components.strong, {
            children: "Remove"
          }), " button. Both changes take effect immediately."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/add-users-to-group.png",
          alt: "Add multiple users to a group"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["If your identity provider manages group membership through SSO group mappings,\nyou can't remove mapped users from those groups in Hightouch. These\nmemberships are labeled ", _jsx(_components.strong, {
            children: "(assigned via SSO)"
          }), ". You can still add users to\nadditional groups in Hightouch."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-group",
        children: "Create a group"
      }), "\n", _jsx(_components.p, {
        children: "Only organization admins can create groups. Create groups for users who need similar workspace access. For example, you might create groups for Data Science and Growth Marketing, or organize groups by team, region, brand, or another structure that affects access."
      }), "\n", _jsx(_components.p, {
        children: "Keep the number of groups manageable by reusing groups when users need overlapping permissions. Users can belong to multiple groups and receive the combined permissions of those groups. For example, instead of creating a separate Growth Marketing Analysts group, you could add those users to both Data Science and Growth Marketing."
      }), "\n", _jsxs(_components.p, {
        children: ["To create a group, go to ", _jsx(_components.strong, {
          children: "Organization settings > Groups"
        }), " and click ", _jsx(_components.strong, {
          children: "Add group"
        }), ". Enter a ", _jsx(_components.strong, {
          children: "Group name"
        }), ", select its members, and click ", _jsx(_components.strong, {
          children: "Add group"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/new-group.png",
          alt: "Creating a new group"
        })
      }), "\n", _jsx(_components.h3, {
        id: "grant-a-group-access-to-a-workspace",
        children: "Grant a group access to a workspace"
      }), "\n", _jsx(_components.p, {
        children: "Only organization admins can give a group access to a workspace. New groups don't have access to any workspaces by default."
      }), "\n", _jsx(_components.p, {
        children: "To grant workspace access:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings > Groups"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select the group."
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Workspaces"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Use the ", _jsx(_components.strong, {
            children: "Role"
          }), " dropdown to assign a role for each workspace the group should access."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/group-workspaces.png",
          alt: "The Workspaces tab of a group in Organization settings, with the Role dropdown open"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A role determines what the group can do within that workspace. You can assign a built-in role, such as ", _jsx(_components.strong, {
          children: "Workspace viewer"
        }), ", or create a custom role. See ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "Roles"
        }), " for details."]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Access"
        }), " column shows the destinations the group has permissions for in each workspace. Hover over a destination to see the group's grants for it. It doesn't include every grant or permissions for other resource types."]
      }), "\n", _jsx(_components.h3, {
        id: "change-a-groups-role-from-workspace-settings",
        children: "Change a group's role from workspace settings"
      }), "\n", _jsx(_components.p, {
        children: "Workspace admins can change the role of a group that already has access to their workspace."
      }), "\n", _jsxs(_components.p, {
        children: ["Go to ", _jsx(_components.strong, {
          children: "Workspace settings > Groups"
        }), ", select a new ", _jsx(_components.strong, {
          children: "Role"
        }), " for the user group, and click ", _jsx(_components.strong, {
          children: "Save changes"
        }), ". Hightouch asks you to confirm the changes before applying them."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/workspace-settings-groups.png",
          alt: "The Groups tab in Workspace settings, with the Role dropdown open for a group"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To give a group access to a workspace it can't currently access, an organization admin must assign it from ", _jsx(_components.strong, {
          children: "Organization settings > Groups"
        }), "."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Some changes require an organization admin to reverse. This includes setting a\ngroup's role to ", _jsx(_components.strong, {
            children: "No access"
          }), " or changing the admin role of a group you belong\nto."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "transfer-organization-admin-access",
        children: "Transfer organization admin access"
      }), "\n", _jsxs(_components.p, {
        children: ["When an organization admin changes roles or leaves your company, add a replacement to the ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " group before removing the existing admin. Organization admins control organization-wide settings, including SSO and SCIM configuration, billing, users, and group permissions."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Keep at least two active organization admins so another admin can manage\naccess if one becomes unavailable."
        })
      }), "\n", _jsx(_components.h3, {
        id: "transfer-access-when-an-organization-admin-is-available",
        children: "Transfer access when an organization admin is available"
      }), "\n", _jsxs(_components.p, {
        children: ["If at least one organization admin can still access Hightouch, they can add a replacement from ", _jsx(_components.strong, {
          children: "Organization settings"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If the new admin isn't a Hightouch user yet, ", _jsx(_components.a, {
          href: "#invite-users",
          children: "invite them"
        }), " and select ", _jsx(_components.strong, {
          children: "Organization admins"
        }), " under ", _jsx(_components.strong, {
          children: "Assign to groups"
        }), ". They get organization admin access when they accept the invitation. If your organization requires SSO, invites are turned off, so have the new admin sign in through your identity provider first."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/invite-user-modal.png",
          alt: "The Invite user modal with group assignment options including Organization admins"
        })
      }), "\n", _jsx(_components.p, {
        children: "To make an existing user an organization admin:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings > Groups"
          }), " and select ", _jsx(_components.strong, {
            children: "Organization admins"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Members"
          }), " tab and click ", _jsx(_components.strong, {
            children: "Add users"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the new admin and click the ", _jsx(_components.strong, {
            children: "Assign"
          }), " button."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The new admin has full organization admin access as soon as you assign them."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/org-admins-group-members.png",
          alt: "The Organization admins group Members tab showing current members and the Add users button"
        })
      }), "\n", _jsx(_components.p, {
        children: "To remove a departing organization admin:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings > Groups"
          }), " and select ", _jsx(_components.strong, {
            children: "Organization admins"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Members"
          }), " tab, select the departing admin and click the ", _jsx(_components.strong, {
            children: "Remove"
          }), " button."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/creation/org-admins-remove-user.png",
          alt: "Selecting a user for removal from the Organization admins group"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch requires at least one organization admin and blocks removing the\nlast one. Add a replacement before removing the last remaining admin."
        })
      }), "\n", _jsx(_components.h3, {
        id: "transfer-access-with-sso-and-scim",
        children: "Transfer access with SSO and SCIM"
      }), "\n", _jsx(_components.p, {
        children: "If your organization uses SSO with SCIM provisioning, manage organization admin membership through your identity provider instead of directly in Hightouch:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In your identity provider, such as Okta or Entra ID, confirm that a group is mapped to the Hightouch ", _jsx(_components.strong, {
            children: "Organization admins"
          }), " group."]
        }), "\n", _jsx(_components.li, {
          children: "Add the new admin to the mapped group and remove the departing admin."
        }), "\n", _jsx(_components.li, {
          children: "SCIM syncs the membership changes to Hightouch."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/sso-group-mappings.png",
          alt: "SSO group mappings showing IdP groups mapped to Hightouch groups, including Organization admins"
        })
      }), "\n", _jsx(_components.p, {
        children: "This keeps your identity provider as the single source of truth for admin access."
      }), "\n", _jsx(_components.h3, {
        id: "recover-access-when-no-organization-admins-are-available",
        children: "Recover access when no organization admins are available"
      }), "\n", _jsx(_components.p, {
        children: "If no organization admins at your company can access Hightouch, for example because all previous admins have left, Hightouch Support must help assign a new admin."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Don't create a new organization."
          }), " Your existing organization, workspaces, and configuration are still available."]
        }), "\n", _jsxs(_components.li, {
          children: ["Have someone from your company ", _jsx(PylonLink, {
            pylonText: "Hello, all of our organization admins have left and we need to assign a new organization admin.",
            linkText: "contact Hightouch Support"
          }), " from a verified company email address."]
        }), "\n", _jsxs(_components.li, {
          children: ["Include:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Your company name and Hightouch organization name or workspace URL"
            }), "\n", _jsx(_components.li, {
              children: "The name and email of the new organization admin"
            }), "\n", _jsx(_components.li, {
              children: "Relevant context, such as when the previous admins left"
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Because organization admins can manage billing and security settings, Hightouch may require additional verification, such as confirmation from an existing commercial or security contact on file, before assigning a new admin."
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
